import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { httpsCallable } from 'firebase/functions'
import CloseIcon from '@mui/icons-material/Close'
import AssessmentIcon from '@mui/icons-material/Assessment'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import { firebase_functions } from '../../common/firebaseConfig'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import PatternSelect from '../form/PatternSelect'
import SymbolSelect from '../form/SymbolSelect'
import PatternVersionTitle from '../backtests/PatternVersionTitle'
import { captureEvent } from '../../posthog'

const TRADING_STYLES = [
  {
    label: 'Trend Following',
    value: 'Trend following using moving averages and momentum indicators',
  },
  {
    label: 'Mean Reversion',
    value: 'Mean reversion using Bollinger Bands, RSI, and/or Stochastic',
  },
  {
    label: 'Breakout',
    value: 'Breakout strategy detecting price moves through support/resistance levels',
  },
  {
    label: 'Momentum',
    value: 'Momentum-based strategy using directional indicators like MACD and ADX',
  },
  {
    label: 'Scalping',
    value: 'Short-term scalping strategy with tight stops and quick exits',
  },
  {
    label: 'Range Trading',
    value:
      'Range-bound strategy identifying overbought/oversold conditions within consolidation zones',
  },
  {
    label: 'Custom',
    value: 'custom',
  },
]

type Mode = 'existing' | 'scratch'

type LaunchAgentDialogProps = {
  open: boolean
  onClose: () => void
  onLaunched?: (sessionId: string) => void
  backtest?: any
}

function LaunchAgentDialog({ open, onClose, onLaunched, backtest }: LaunchAgentDialogProps) {
  const theme = useTheme()
  const { tradingHouse } = useApplicationSuiteContext()

  const [mode, setMode] = useState<Mode>('scratch')
  const [selectedPattern, setSelectedPattern] = useState<any>(null)
  const [stylePreset, setStylePreset] = useState<string>('')
  const [customStyle, setCustomStyle] = useState<string>('')
  const [symbol, setSymbol] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [launching, setLaunching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // When launched from a backtest, pre-populate symbol from backtestSettings
  useEffect(() => {
    if (open && backtest) {
      const settings = backtest.backtestSettings || {}
      if (settings.executionSymbol) setSymbol(settings.executionSymbol)
    }
  }, [open, backtest])

  const handleModeChange = (next: Mode) => {
    setMode(next)
    if (next === 'scratch') {
      setSelectedPattern(null)
    } else {
      setStylePreset('')
      setCustomStyle('')
    }
  }

  const effectiveStyle =
    stylePreset === 'custom'
      ? customStyle
      : TRADING_STYLES.find((s) => s.label === stylePreset)?.value || ''

  const isValid = backtest
    ? !!symbol
    : mode === 'existing'
      ? !!symbol && !!selectedPattern
      : !!symbol && !!effectiveStyle

  const handleLaunch = async () => {
    if (!isValid) return
    setLaunching(true)
    setError(null)

    try {
      const sessionId = crypto.randomUUID()

      const noteParts: string[] = []
      if (selectedPattern) {
        noteParts.push(
          `START FROM EXISTING PATTERN: id=${selectedPattern.id}, version=${selectedPattern.version}, name="${selectedPattern.name}". ` +
            `Load this pattern using get_pattern and use it as your starting point for optimization.`
        )
      }
      if (notes.trim()) {
        noteParts.push(notes.trim())
      }

      const tfSource = backtest?.backtestSettings ?? selectedPattern?.trigger?.chartDetails
      const periodicity = tfSource?.periodicity ?? 1
      const timeframe = tfSource?.timeframe ?? 'hour'

      const config: Record<string, any> = {
        symbol,
        periodicity,
        timeframe,
        style: effectiveStyle || 'Optimize the existing strategy based on backtest analysis',
        notes: noteParts.length > 0 ? noteParts.join('\n\n') : undefined,
      }

      if (backtest) {
        config.existingBacktestId = backtest.id
        config.existingPatternId = backtest.pattern?.id
        config.existingPatternVersion = backtest.pattern?.version
      }

      const callable = httpsCallable(firebase_functions, 'strategyagent')
      callable({ config, tradingHouse, sessionId }).catch((err) => {
        console.error('[strategyAgent] error:', err)
      })

      captureEvent('agent_session_launched', {
        has_existing_backtest: Boolean(backtest),
        timeframe,
        periodicity,
      })
      onLaunched?.(sessionId)
      handleClose()
    } catch (err: any) {
      setError(err.message || 'Failed to launch agent')
      setLaunching(false)
    }
  }

  const handleClose = () => {
    if (launching) return
    setMode('scratch')
    setSelectedPattern(null)
    setStylePreset('')
    setCustomStyle('')
    if (!backtest) setSymbol('')
    setNotes('')
    setLaunching(false)
    setError(null)
    onClose()
  }

  const tooltipTitle = !isValid
    ? backtest
      ? 'Select a symbol to continue'
      : mode === 'existing'
        ? 'Select a pattern and symbol to continue'
        : 'Select a symbol and style to continue'
    : ''

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SmartToyOutlinedIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
            {backtest ? 'Run Agent on Backtest' : 'New Agent Session'}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={launching}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {backtest
            ? "The agent will review this backtest's results, identify weaknesses in the current strategy, and run iterations to improve it, creating new pattern versions along the way."
            : mode === 'existing'
              ? 'The agent will take the selected pattern, run backtests, analyze the results, then make pattern or backtest settings improvements and rerun until performance thresholds are met.'
              : 'The agent will build a new strategy using the selected style, run backtests, analyze the results, then make pattern or backtest settings improvements and rerun until performance thresholds are met.'}
        </Typography>

        {/* Backtest mode — read-only starting point styled like the card header */}
        {backtest ? (
          <Box
            px={1.5}
            py={1.25}
            sx={{
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.custom4,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} overflow="hidden">
              <AssessmentIcon fontSize="small" sx={{ flexShrink: 0 }} />
              <Typography variant="body1" noWrap>
                {backtest.backtestSettings?.name || 'Unnamed Backtest'}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} overflow="hidden">
              <AccountTreeIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
              <PatternVersionTitle
                pattern={backtest.pattern}
                symbol={backtest.backtestSettings?.executionSymbol}
                color="text.secondary"
                compact
              />
            </Box>
          </Box>
        ) : null}

        {!backtest && (
          <>
            {/* Mode selector */}
            <Box display="flex" gap={1.5}>
              {(
                [
                  {
                    key: 'scratch',
                    label: 'Create new',
                    icon: <AddOutlinedIcon sx={{ fontSize: 18 }} />,
                    sub: 'Build a strategy from scratch',
                  },
                  {
                    key: 'existing',
                    label: 'Edit existing',
                    icon: <EditOutlinedIcon sx={{ fontSize: 18 }} />,
                    sub: 'Iterate on a pattern you already have',
                  },
                ] as const
              ).map(({ key, label, icon, sub }) => {
                const selected = mode === key
                return (
                  <Box
                    key={key}
                    onClick={() => handleModeChange(key)}
                    sx={{
                      flex: 1,
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                      backgroundColor: selected ? `${theme.palette.primary.main}14` : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: selected
                          ? theme.palette.primary.main
                          : theme.palette.text.disabled,
                        backgroundColor: selected
                          ? `${theme.palette.primary.main}1A`
                          : theme.palette.action.hover,
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      mb={0.5}
                      sx={{ color: selected ? theme.palette.primary.main : 'text.secondary' }}
                    >
                      {icon}
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={selected ? 'primary' : 'text.primary'}
                      >
                        {label}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                      {sub}
                    </Typography>
                  </Box>
                )
              })}
            </Box>

            {/* Mode-specific fields */}
            {mode === 'existing' ? (
              <PatternSelect
                pattern={selectedPattern}
                onSelect={setSelectedPattern}
                helperText="Choose a pattern for the agent to optimize"
              />
            ) : (
              <Box display="flex" flexDirection="column" gap={1}>
                <FormControl fullWidth>
                  <InputLabel id="style-label">Trading Style</InputLabel>
                  <Select
                    labelId="style-label"
                    value={stylePreset}
                    label="Trading Style"
                    onChange={(e) => setStylePreset(e.target.value)}
                  >
                    {TRADING_STYLES.map((style) => (
                      <MenuItem key={style.label} value={style.label}>
                        {style.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 0.5, lineHeight: 1.5 }}
                >
                  The trading style guides the initial strategy design. The agent will refine it
                  based on backtest results.
                </Typography>
                {stylePreset === 'Custom' && (
                  <TextField
                    fullWidth
                    label="Describe the strategy"
                    value={customStyle}
                    onChange={(e) => setCustomStyle(e.target.value)}
                    placeholder="e.g. RSI divergence with candlestick confirmation"
                    multiline
                    rows={2}
                  />
                )}
              </Box>
            )}
          </>
        )}

        {/* Symbol */}
        {!backtest && <SymbolSelect symbol={symbol} onSelect={(s) => setSymbol(s?.name || '')} />}

        {/* Notes */}
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
          >
            Additional Instructions
          </Typography>
          <Box mt={1}>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe any specific requirements, preferred indicators, exit strategy preferences, or other context..."
            />
          </Box>
        </Box>

        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={launching}>
          Cancel
        </Button>
        <Tooltip title={tooltipTitle} placement="top">
          <span>
            <Button
              variant="contained"
              onClick={handleLaunch}
              disabled={!isValid || launching}
              startIcon={
                launching ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SmartToyOutlinedIcon />
                )
              }
            >
              {launching ? 'Launching…' : 'Launch Agent'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  )
}

export default LaunchAgentDialog
